import { formatNumber } from 'i18n/format';
import { useTranslation } from 'react-i18next';
import { t as translate } from 'i18n';
import React, { useMemo } from 'react';
import { Alert, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import MarkdownText from './MarkdownText';
import { modelTranscript } from './modelTranscript';
import { diagnosticResult, toolLabel } from './diagnosticResults';

const MODEL_ERRORS = () => ({
  provider_truncated: translate('assistant.diagnosticOutputTheModelSAnswerWasInterruptedThe'),
  provider_timeout: translate('assistant.diagnosticOutputTheModelDidNotFinishItsAnswer'),
  provider_bad_output: translate('assistant.diagnosticOutputTheModelCouldNotFinishTheAction'),
  provider_refusal: translate('assistant.diagnosticOutputTheModelRefusedToContinueTheAnswer'),
});
const PARSE_ERRORS = () => ({
  invalid_sse_json: translate('assistant.diagnosticOutputAChunkOfTheModelSAnswer'),
  invalid_sse_chunk: translate('assistant.diagnosticOutputCouldNotReadAChunkOfThe'),
  invalid_choices: translate('assistant.diagnosticOutputCouldNotReadTheStructureOfThe'),
  invalid_choice: translate('assistant.diagnosticOutputCouldNotReadAVariantOfThe'),
  invalid_delta: translate('assistant.diagnosticOutputCouldNotReadAnUpdateToThe'),
  invalid_tool_calls: translate('assistant.diagnosticOutputCouldNotReadTheModelSAction'),
  invalid_tool_call: translate('assistant.diagnosticOutputCouldNotReadTheModelSAction2'),
  invalid_tool_call_index: translate('assistant.diagnosticOutputCouldNotDetermineTheOrderOfThe'),
  missing_tool_name: translate('assistant.diagnosticOutputTheModelDidNotProvideAnAction'),
  invalid_tool_arguments_json: translate('assistant.diagnosticOutputTheModelSActionArgumentsContainInvalid'),
  tool_arguments_not_object: translate('assistant.diagnosticOutputTheModelSActionArgumentsMustBe'),
  invalid_tool_call_id: translate('assistant.diagnosticOutputCouldNotDetermineTheModelSAction'),
  sdk_bad_output: translate('assistant.diagnosticOutputCouldNotReadTheModelSAnswer'),
});

function parseErrorText(detail) {
  const reason = typeof detail?.reason === 'string' && Object.hasOwn(PARSE_ERRORS(), detail.reason) ? PARSE_ERRORS()[detail.reason] : translate('assistant.diagnosticOutputCouldNotReadTheModelSAnswer');
  const lines = [reason];
  if (typeof detail?.name === 'string' && /^[a-zA-Z_][a-zA-Z0-9_.:-]{0,127}$/.test(detail.name)) {
    const name = toolLabel(detail.name);
    lines.push(translate('assistant.diagnosticOutputAction', { value1: typeof name !== 'string' || name === translate('assistant.diagnosticOutputActionResult') ? detail.name : `${name} (${detail.name})` }));
  }
  const integer = (value, minimum) => Number.isSafeInteger(value) && value >= minimum;
  if (integer(detail?.line, 1)) lines.push(translate('assistant.diagnosticOutputLine', { value1: detail.line, value2: integer(detail?.column, 1) ? translate('assistant.diagnosticOutputColumn', { value1: detail.column }) : '' }));
  else if (integer(detail?.position, 0)) lines.push(translate('assistant.diagnosticOutputPositionZeroBased', { value1: detail.position }));
  return lines.join('\n');
}

function cellText(value, column) {
  if (value == null) return '—';
  if (typeof value === 'boolean') return value ? translate('assistant.diagnosticOutputYes') : translate('assistant.diagnosticOutputNo');
  if (typeof value === 'number') {
    const unit = column?.format === 'currency' ? ' ₽' : column?.format === 'percent' ? ' %' : '';
    return `${formatNumber(value, { maximumFractionDigits: 20 })}${unit}`;
  }
  if (Array.isArray(value)) return value.map((item) => cellText(item)).join('\n');
  if (typeof value === 'object') return Object.entries(value).map(([key, item]) => `${key}: ${cellText(item)}`).join('\n');
  return String(value);
}

function ResultNode({ node, nested = false }) {
  const { t: translate } = useTranslation();
  if (!node || typeof node !== 'object') return null;
  return (
    <Box sx={{ mt: nested ? 1.5 : 0, minWidth: 0 }}>
      {node.title && <Typography variant={nested ? 'subtitle2' : 'body2'} fontWeight={600} sx={{ mb: 0.5 }}>{node.title}</Typography>}
      {node.text && <MarkdownText text={node.text} />}
      {(node.tables || []).map((table, index) => (
        <Box key={index} sx={{ mt: 1 }}>
          {table.title && <Typography variant="caption" fontWeight={600}>{table.title}</Typography>}
          <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
            <Table size="small" aria-label={table.title || node.title || translate('assistant.diagnosticOutputQueryResult')}>
              <TableHead>
                <TableRow>{table.columns.map((column) => <TableCell key={column.key}>{column.title || column.key}</TableCell>)}</TableRow>
              </TableHead>
              <TableBody>
                {table.rows.map((row, number) => (
                  <TableRow key={number}>
                    {table.columns.map((column, col) => (
                      <TableCell key={column.key} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                        {cellText(Array.isArray(row) ? row[col] : row[column.key], column)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
      {(node.sections || []).map((section, index) => <ResultNode key={index} node={section} nested />)}
    </Box>
  );
}

/** Keep an interleaved final message in its place instead of repeating it below. */
export function finalOutputAvailable(entries, text) {
  if (!Array.isArray(entries) || typeof text !== 'string' || !text.trim()) return false;
  const last = [...entries].reverse().find((entry) => entry?.kind === 'model');
  return modelTranscript(last).filter((block) => block.kind === 'text').map((block) => block.text).join('').trim() === text.trim();
}

const ModelBlock = React.memo(function ModelBlock({ block, finalAnswer, source }) {
  const { t: translate } = useTranslation();
  return (
    <Box data-output-kind={block.kind} sx={{ pl: 1.5, py: 1, mb: 1, borderLeft: 2, borderColor: block.kind === 'reasoning' ? 'primary.main' : 'divider' }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {block.kind === 'reasoning' ? translate('assistant.diagnosticOutputReasoning') : finalAnswer ? translate('assistant.diagnosticOutputModelAnswer') : translate('assistant.diagnosticOutputModelMessage')}
      </Typography>
      <Box data-final-answer={finalAnswer ? 'true' : undefined} data-quote-source={JSON.stringify(finalAnswer ? { kind: 'final' } : source)} data-quote-label={block.kind === 'reasoning' ? translate('assistant.diagnosticOutputReasoning') : finalAnswer ? undefined : translate('assistant.diagnosticOutputModelMessage')}><MarkdownText text={block.text} /></Box>
    </Box>
  );
});

const ModelEntry = React.memo(function ModelEntry({ entry, entryIndex, finalText }) {
  const { t: translate } = useTranslation();
  const blocks = useMemo(() => modelTranscript(entry), [entry]);
  const text = useMemo(() => blocks.filter((block) => block.kind === 'text').map((block) => block.text).join('').trim(), [blocks]);
  const finalAnswer = typeof finalText === 'string' && Boolean(finalText.trim()) && text === finalText.trim();
  return (
    <>
      {blocks.map((block, index) => <ModelBlock key={index} block={block} finalAnswer={block.kind === 'text' && finalAnswer} source={{ kind: 'diagnostic_model', entry_index: entryIndex, block_index: index }} />)}
      {(entry.error || entry.parse_error) && <Alert severity="warning" sx={{ mb: 1, whiteSpace: 'pre-line' }}>{entry.parse_error ? parseErrorText(entry.parse_error) : MODEL_ERRORS()[entry.error] || translate('assistant.diagnosticOutputCouldNotCompleteTheModelSAnswer')}</Alert>}
    </>
  );
});

const ToolEntry = React.memo(function ToolEntry({ entry, entryIndex }) {
  const { t: translate } = useTranslation();
  const result = diagnosticResult(entry);
  return (
    <Box data-output-kind="result" sx={{ pl: 1.5, py: 1, mb: 1, borderLeft: 2, borderColor: entry.is_error ? 'warning.main' : 'divider' }}>
      <Box data-quote-source={JSON.stringify({ kind: 'diagnostic_tool', entry_index: entryIndex })} data-quote-label={translate('assistant.diagnosticOutputQueryResult')}><ResultNode node={result} /></Box>
    </Box>
  );
});

/** Readable model thoughts, intermediate messages and results in execution order. */
export default React.memo(function DiagnosticOutput({ entries, enabled = false, finalText = '' }) {
  const { t: translate } = useTranslation();
  const records = useMemo(() => (Array.isArray(entries) ? entries : []).map((entry, index) => ({ entry, index })).filter(({ entry }) => entry && typeof entry === 'object'), [entries]);
  if (!enabled && records.length === 0) return null;
  const lastModel = records.reduce((last, { entry }, index) => entry.kind === 'model' ? index : last, -1);
  return (
    <Box component="section" aria-label={translate('assistant.diagnosticOutputReasoningAndResults')} sx={{ mb: 2, minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{translate('assistant.diagnosticOutputReasoningAndResults')}</Typography>
      {records.map(({ entry, index: originalIndex }, index) => {
        if (entry.kind === 'tool') return <ToolEntry key={originalIndex} entry={entry} entryIndex={originalIndex} />;
        if (entry.kind !== 'model') return null;
        return <ModelEntry key={originalIndex} entry={entry} entryIndex={originalIndex} finalText={index === lastModel ? finalText : ''} />;
      })}
      {records.length === 0 && <Typography variant="body2" color="text.secondary">{translate('assistant.diagnosticOutputReasoningAndResultsWillAppearAsThe')}</Typography>}
    </Box>
  );
});
